import { useParams, Link, useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, ArrowRight, CheckCircle, User, ChevronDown, BookOpen } from 'lucide-react';
import { useState } from 'react';
import { Toaster, toast } from 'sonner';
import { useTheme } from '../context/ThemeContext';
import { trainings } from '../data/trainings';
import { trainingDetails } from '../data/trainingDetails';
import TrainerVideoEmbed from '../components/TrainerVideoEmbed';

const TrainingDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const training = trainings.find(t => t.id === parseInt(id));
  const details = trainingDetails[id];

  const [expandedParts, setExpandedParts] = useState(() => {
    if (details?.trainingStructure) {
      return Object.fromEntries(details.trainingStructure.map(p => [p.part, true]));
    }
    return {};
  });

  const togglePart = (partNumber) => {
    setExpandedParts(prev => ({
      ...prev,
      [partNumber]: !prev[partNumber]
    }));
  };

  if (!training || !details) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-[var(--text-main)] mb-4">Training Not Found</h1>
          <Link to="/services/quarterly-trainings" className="text-[var(--primary)] hover:underline">Back to Trainings</Link>
        </div>
      </div>
    );
  }

  const handleRegister = () => {
    navigate('/services/quarterly-trainings/register');
  };

  return (
    <div className="min-h-screen pt-20" data-testid="training-detail-page">
      <Toaster position="top-right" theme={theme} toastOptions={{ style: { background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-main)' } }} />

      {/* Hero */}
      <section className="py-10 md:py-16 relative overflow-hidden border-b border-[var(--border)]">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="container-main relative z-10">
          <Link to="/services/quarterly-trainings" className="inline-flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] mb-6 font-mono text-sm">
            <ArrowRight className="w-4 h-4 rotate-180" /> Back to Trainings
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <span className={`inline-block px-3 py-1 text-xs font-mono uppercase tracking-wider mb-4 ${
                training.level === 'Beginner' ? 'bg-[var(--primary)]/20 text-[var(--primary)]' :
                training.level === 'Intermediate' ? 'bg-[var(--accent-cyan)]/20 text-[var(--accent-cyan)]' :
                training.level === 'Advanced' ? 'bg-[var(--accent-purple)]/20 text-[var(--accent-purple)]' :
                'bg-[var(--error)]/20 text-[var(--error)]'
              }`}>{training.level}</span>
              <h1 className="text-3xl md:text-5xl font-bold text-[var(--text-main)] mb-6">{training.title}</h1>
              <div className="text-[var(--text-muted)] text-lg leading-relaxed mb-6 space-y-4">
                {details.summary.split('\n\n').map((paragraph, idx) => (
                  <p key={idx}>{paragraph}</p>
                ))}
              </div>
              <div className="flex flex-wrap gap-2">
                {training.tags.map((tag, idx) => (
                  <span key={idx} className="px-3 py-1 bg-[var(--primary)]/10 border border-[var(--primary)]/30 text-sm text-[var(--primary)] font-mono">{tag}</span>
                ))}
              </div>
            </div>
            <div className="bg-[var(--surface)] border border-[var(--border)] p-6">
              <div className="space-y-4 mb-6">
                <div className="flex items-center gap-3"><Calendar className="w-5 h-5 text-[var(--primary)]" /><div><p className="text-xs text-[var(--text-muted)]">Date</p><p className="text-[var(--text-main)] font-mono">{training.date}</p></div></div>
                <div className="flex items-center gap-3"><Clock className="w-5 h-5 text-[var(--primary)]" /><div><p className="text-xs text-[var(--text-muted)]">Time</p><p className="text-[var(--text-main)] font-mono">{training.time}</p></div></div>
                <div className="flex items-center gap-3"><MapPin className="w-5 h-5 text-[var(--primary)]" /><div><p className="text-xs text-[var(--text-muted)]">Location</p><p className="text-[var(--text-main)] font-mono">{training.venue}</p></div></div>
                <div className="flex items-center gap-3"><BookOpen className="w-5 h-5 text-[var(--primary)]" /><div><p className="text-xs text-[var(--text-muted)]">Training Days</p><p className="text-[var(--text-main)] font-mono">{training.duration}</p></div></div>
              </div>
              {training.status !== 'archived' && (
                <div className="border-t border-[var(--border)] pt-6">
                  {/* <p className="text-xs text-[var(--text-muted)] mb-1">Investment</p>
                  <p className="font-mono text-4xl font-bold text-[var(--primary)] mb-6">{training.price}</p> */}
                  <button
                    onClick={handleRegister}
                    className="w-full bg-[var(--primary)] text-black font-mono font-bold py-3 px-4 uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_15px_var(--glow-primary)]"
                  >
                    Register
                  </button>
                  {training.businessCasePdf && (
                    <a
                      href={training.businessCasePdf}
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 w-full block text-center bg-[var(--primary)] text-black font-mono font-bold py-3 px-4 uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_15px_var(--glow-primary)]"
                    >
                      Download Manager Approval Doc
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Meet Your Trainer */}
      {details.trainerVideo && (
        <section className="py-10 border-b border-[var(--border)]" data-testid="trainer-video-section">
          <div className="container-main">
            <p className="font-mono text-[var(--primary)] text-xl uppercase tracking-wider mb-6 text-center">./meet_your_trainer</p>
            <TrainerVideoEmbed url={details.trainerVideo.url} caption={details.trainerVideo.caption} />
          </div>
        </section>
      )}

      {/* Is This For You */}
      {details.selfCheckQuestions && (
        <section className="py-10 border-b border-[var(--border)]" data-testid="self-check-section">
          <div className="container-main">
            <p className="font-mono text-[var(--primary)] text-xl uppercase tracking-wider mb-6">./is_this_for_you</p>
            <div className="bg-[var(--surface)] border border-[var(--border)]">
              <div className="p-6 border-b border-[var(--border)]">
                <h2 className="text-l font-bold text-[var(--text-main)]">{details.selfCheckQuestions.mainQuestion}</h2>
              </div>
              <ul className="space-y-0">
                {details.selfCheckQuestions.questions.map((question, idx) => (
                  <li key={idx} className="flex items-start gap-3 p-4 pl-4 border-l-2 border-l-[var(--primary)]">
                    <CheckCircle className="w-4 h-4 text-[var(--primary)] flex-shrink-0 mt-1" />
                    <span className="text-[var(--text-main)]">{question}</span>
                  </li>
                ))}
              </ul>
              <div className="p-6 border-t border-[var(--border)]">
                <h2 className="text-l font-bold text-[var(--text-main)]">If so, this course is for you.</h2>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Topics */}
      <section className="py-10 border-b border-[var(--border)]" data-testid="topics-section">
        <div className="container-main">
          <p className="font-mono text-[var(--primary)] text-xl uppercase tracking-wider mb-6">./topics_covered</p>
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {details.topics.map((topic, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-[var(--primary)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-main)]">{topic}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Training Curriculum */}
      {details.trainingStructure && (
        <section className="py-10 border-b border-[var(--border)]" data-testid="curriculum-section">
          <div className="container-main">
            <p className="font-mono text-[var(--primary)] text-xl uppercase tracking-wider mb-8">./training_curriculum</p>
            <div className="space-y-4">
              {details.trainingStructure.map((part, idx) => (
                <div key={idx} className="border border-[var(--border)] bg-[var(--surface)]">
                  <button
                    onClick={() => togglePart(part.part)}
                    className="w-full text-left p-6 hover:bg-[var(--surface-highlight)] transition-colors duration-200 flex items-center justify-between"
                  >
                    <div>
                      <h3 className="text-lg font-bold text-[var(--primary)] font-mono mb-1">Part {part.part}</h3>
                      <p className="text-[var(--text-main)] font-semibold">{part.title}</p>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-[var(--text-muted)] transition-transform duration-300 flex-shrink-0 ${
                        expandedParts[part.part] ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {expandedParts[part.part] && (
                    <div className="border-t border-[var(--border)] p-6 bg-[var(--surface)]/50">
                      {part.description ? (
                        <div className="space-y-4 text-[var(--text-main)] leading-relaxed">
                          {part.description.split('\n\n').map((paragraph, paraIdx) => (
                            <p key={paraIdx}>{paragraph}</p>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {part.topics?.map((topic, topicIdx) => (
                            <div key={topicIdx} className="flex items-start gap-3 pl-4 border-l-2 border-[var(--primary)]">
                              <CheckCircle className="w-4 h-4 text-[var(--primary)] flex-shrink-0 mt-1" />
                              <span className="text-[var(--text-main)]">{topic}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Who is this for */}
      <section className="py-10 border-b border-[var(--border)]" data-testid="suitable-section">
        <div className="container-main">
          <p className="font-mono text-[var(--primary)] text-xl uppercase tracking-wider mb-6">./who_is_this_for</p>
          <ul className="space-y-3">
            {details.suitableFor.map((item, idx) => (
              <li key={idx} className="flex items-start gap-3">
                <User className="w-5 h-5 text-[var(--accent-cyan)] flex-shrink-0 mt-0.5" />
                <span className="text-[var(--text-main)]">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-10 border-b border-[var(--border)]" data-testid="benefits-section">
        <div className="container-main">
          <p className="font-mono text-[var(--primary)] text-xl uppercase tracking-wider mb-6">./what_you_will_gain</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {details.benefits.map((benefit, idx) => (
              <div key={idx} className="p-4 bg-[var(--surface)] border border-[var(--border)]">
                <CheckCircle className="w-6 h-6 text-[var(--primary)] mb-3" />
                <p className="text-[var(--text-main)]">{benefit}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training Requirements */}
      {details.requirements && (
        <section className="py-10 border-b border-[var(--border)]">
          <div className="container-main">
            <p className="font-mono text-[var(--primary)] text-xl uppercase tracking-wider mb-6">./requirements_and_prep</p>
            <ul className="space-y-3">
              {details.requirements.map((req, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[var(--accent-purple)] flex-shrink-0 mt-0.5" />
                  <span className="text-[var(--text-main)]">{req}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* What to Expect */}
      {details.whatToExpect && (
        <section className="py-10 border-b border-[var(--border)]">
          <div className="container-main">
            <p className="font-mono text-[var(--primary)] text-xl uppercase tracking-wider mb-6">./what_to_expect</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {details.whatToExpect.map((item, idx) => (
                <div key={idx} className="p-4 bg-[var(--surface)] border border-[var(--border)]">
                  <CheckCircle className="w-5 h-5 text-[var(--primary)] mb-3" />
                  <p className="text-[var(--text-main)]">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* What NOT to Expect */}
      {details.whatNotToExpect && (
        <section className="py-10 border-b border-[var(--border)]">
          <div className="container-main">
            <p className="font-mono text-[var(--primary)] text-xl uppercase tracking-wider mb-6">./what_not_to_expect</p>
            <ul className="space-y-3">
              {details.whatNotToExpect.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="text-[var(--error)] text-xl font-bold mt-0.5">✕</span>
                  <span className="text-[var(--text-main)]">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* What You Will Receive */}
      {details.whatYouWillReceive && (
        <section className="py-10 border-b border-[var(--border)]">
          <div className="container-main">
            <p className="font-mono text-[var(--primary)] text-xl uppercase tracking-wider mb-6">./what_you_will_receive</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {details.whatYouWillReceive.map((item, idx) => (
                <div key={idx} className="p-4 bg-[var(--surface)] border border-[var(--border)]">
                  <CheckCircle className="w-5 h-5 text-[var(--accent-cyan)] mb-3" />
                  <p className="text-[var(--text-main)]">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Instructors */}
      <section className="py-10 border-b border-[var(--border)]" data-testid="instructor-section">
        <div className="container-main">
          <p className="font-mono text-[var(--primary)] text-xl uppercase tracking-wider mb-6">./instructors</p>
          <div className="space-y-8">
            {training.instructors.map((instructor, idx) => (
              <div key={idx} className="flex items-start gap-6">
                {instructor.photo ? (
                  <img
                    src={instructor.photo}
                    alt={instructor.name}
                    className="w-20 h-20 rounded-full object-cover border border-[var(--primary)] flex-shrink-0"
                    loading="lazy"
                    decoding="async"
                  />
                ) : (
                  <div className="w-20 h-20 bg-[var(--surface-highlight)] border border-[var(--primary)] flex items-center justify-center font-mono text-[var(--primary)] text-2xl font-bold flex-shrink-0">
                    {instructor.name.split(' ').map(n => n[0]).join('')}
                  </div>
                )}
                <div>
                  <h3 className="text-2xl font-bold text-[var(--text-main)] mb-1">{instructor.name}</h3>
                  {(instructor.designation || instructor.company) && (
                    <p className="text-[var(--primary)] text-sm font-mono mb-2">
                      {instructor.designation && instructor.company
                        ? `${instructor.designation} @ ${instructor.company}`
                        : instructor.designation || instructor.company}
                    </p>
                  )}
                  <p className="text-[var(--text-muted)] leading-relaxed">{instructor.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Register CTA */}
      {training.status !== 'archived' && (
        <section className="py-8 md:py-12 relative overflow-hidden" data-testid="register-section">
          <div className="absolute inset-0 grid-bg opacity-20" />
          <div className="hero-glow bottom-0 left-1/2 -translate-x-1/2" />
          <div className="container-main relative z-10 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-main)] mb-4">Ready to Level Up?</h2>
            <p className="text-[var(--text-muted)] text-lg max-w-2xl mx-auto mb-8">
              Secure your spot in {training.title}.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={handleRegister}
                className="bg-[var(--primary)] text-black font-mono font-bold px-12 py-4 text-lg uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_25px_var(--glow-primary)]"
                data-testid="register-btn"
              >
                Register Now
              </button>
              {training.businessCasePdf && (
                <a
                  href={training.businessCasePdf}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[var(--primary)] text-black font-mono font-bold px-12 py-4 text-lg uppercase tracking-widest hover:bg-[var(--primary-dim)] transition-colors duration-200 shadow-[0_0_15px_var(--glow-primary)]"
                >
                  Download Manager Approval Doc
                </a>
              )}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default TrainingDetailPage;
